import { defineStore } from 'pinia'
import type { BillingConfigItem, CreditState } from './helper'
import { getLocalState, setLocalState } from './helper'
import { getBillingForUser, getListBillingConfig } from '@/api/billing'

export const useCreditStore = defineStore('credit-store', {
  state: (): CreditState => getLocalState(),

  getters: {
    // Get basic credit balance
    basicCreditBalance: state => state.creditData?.base_credit_balance || 0,

    // Get advanced credit balance
    advancedCreditBalance: state => state.creditData?.advance_credit_balance || 0,

    // Latest transient usage preview (do not affect balances)
    usagePreview: state => state.usagePreview,

    // Get total credits spent
    totalCreditsSpent: state => state.creditData?.total_credits_spent || 0,

    // Get expired wallets count
    expiredWallets: state => state.creditData?.expired_wallets || 0,

    // Check if credit data exists
    hasCreditData: state => state.creditData !== null && state.creditData !== undefined,

    // Check if data is stale (older than 5 minutes)
    isDataStale: (state) => {
      if (!state.lastUpdated)
        return true
      const fiveMinutes = 5 * 60 * 1000 // 5 minutes in milliseconds
      return Date.now() - state.lastUpdated > fiveMinutes
    },

    // Get billing configs by code_config
    getBillingConfigsByCode: state => (codeConfig: string) => {
      return state.billingConfigs.filter(item => item.config.code_config === codeConfig)
    },

    // Get all billing configs
    allBillingConfigs: state => state.billingConfigs,

    // Check if billing configs exist
    hasBillingConfigs: state => state.billingConfigs.length > 0,

    // Check if wallet is created for first time
    isWalletCreated: state => state.isCreatedWallet,
  },

  actions: {
    // Set credit data
    setCreditData(creditData: any) {
      this.creditData = creditData
      this.lastUpdated = Date.now()
      this.recordState()
    },

    // Reset credit data
    resetCreditData() {
      this.creditData = null
      this.isLoading = false
      this.lastUpdated = null
      this.recordState()
    },

    // Set loading state
    setLoading(loading: boolean) {
      this.isLoading = loading
      this.recordState()
    },

    // Set isCreatedWallet state
    setIsCreatedWallet(value: boolean) {
      this.isCreatedWallet = value
      this.recordState()
    },

    // Reset isCreatedWallet to false (after showing welcome bonus)
    resetIsCreatedWallet() {
      this.isCreatedWallet = true
      this.recordState()
    },

    // Fetch credit data from API
    async fetchCreditData(forceRefresh = false) {
      // Don't fetch if already loading
      // if (this.isLoading)
      //   return

      // Don't fetch if data exists and is not stale, unless forced
      // if (!forceRefresh && this.hasCreditData && !this.isDataStale)
      //   return this.creditData
      try {
        this.setLoading(true)
        const response = await getBillingForUser()

        if (response.data && response.success) {
          this.setCreditData(response.data)
          return response.data
        }
        else {
          console.error('Failed to fetch credit data:', response.data?.message || 'Unknown error')
          return null
        }
      }
      catch (error) {
        console.error('Error fetching credit data:', error)
        return null
      }
      finally {
        this.setLoading(false)
      }
    },

    // Update credit balances (for real-time updates from BE)
    updateCreditBalances(basicCredit: number, advancedCredit: number) {
      // Ensure creditData exists so realtime billing updates are not dropped
      if (!this.creditData) {
        this.creditData = {
          user_id: '',
          base_credit_balance: 0,
          advance_credit_balance: 0,
          total_credits_spent: 0,
          expired_wallets: 0,
        }
      }

      this.creditData.base_credit_balance = basicCredit
      this.creditData.advance_credit_balance = advancedCredit
      this.lastUpdated = Date.now()
      this.recordState()
    },

    // Set billing configs
    setBillingConfigs(configs: BillingConfigItem[]) {
      this.billingConfigs = configs
      this.recordState()
    },

    // Reset billing configs
    resetBillingConfigs() {
      this.billingConfigs = []
      this.recordState()
    },

    // Fetch billing configs from API
    async fetchBillingConfigs(params?: { billing_unit?: string; target_type?: string; service_name?: string }) {
      try {
        this.setLoading(true)
        const response = await getListBillingConfig(params || {})

        if (response.data && response.success) {
          this.setBillingConfigs(response.data)
          return response.data
        }
        else {
          console.error('Failed to fetch billing configs:', response.data?.message || 'Unknown error')
          return null
        }
      }
      catch (error) {
        console.error('Error fetching billing configs:', error)
        return null
      }
      finally {
        this.setLoading(false)
      }
    },

    // Get credit cost for specific code_config and target_key
    getCreditCost(codeConfig: string, targetKey?: string) {
      const configs = this.getBillingConfigsByCode(codeConfig)

      if (targetKey) {
        const filteredConfigs = configs.filter(item => item.config.target_key === targetKey)
        return filteredConfigs.map(config => ({
          base_credit_cost: config.config.base_credit_cost,
          advance_credit_cost: config.config.advance_credit_cost,
          required_credit_type: config.config.required_credit_type,
          billing_unit: config.config.billing_unit,
          target_key: config.config.target_key,
        }))
      }

      // Return all configs if no target_key specified
      return configs.map(config => ({
        base_credit_cost: config.config.base_credit_cost,
        advance_credit_cost: config.config.advance_credit_cost,
        required_credit_type: config.config.required_credit_type,
        billing_unit: config.config.billing_unit,
        target_key: config.config.target_key,
      }))
    },

    // Set latest usage preview (transient info; does not affect balances)
    setUsagePreview(payload: CreditState['usagePreview']) {
      this.usagePreview = payload
      this.recordState()
    },

    clearUsagePreview() {
      this.usagePreview = null
      this.recordState()
    },

    // Save state to localStorage
    recordState() {
      setLocalState(this.$state)
    },
  },
})
